import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Copy, CornerUpLeft, Flag, Trash2 } from "lucide-react";
import { idEq } from "../../utils/bondchat/chatReadHelpers";

export const QUICK_REACTIONS = ["❤️", "😂", "🔥", "👏", "😮", "⚽"];

const MessageActionMenu = ({
  message,
  userId,
  position,
  onClose,
  onReact,
  onReply,
  onReport,
  onDelete,
  canReply = true,
}) => {
  const menuRef = useRef(null);

  useEffect(() => {
    if (!position) return undefined;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose?.();
    };
    const handleKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKey);
    };
  }, [position, onClose]);

  if (!message || !position) return null;

  const isMine = idEq(message.userId?._id ?? message.userId, userId);
  const messageKey = message._id || message.tempId;
  const hasText = !!message.message?.trim();
  const myReactions = new Set(
    (message.reactions || [])
      .filter((r) => (r.users || []).some((id) => idEq(id, userId)))
      .map((r) => r.emoji)
  );

  const handleCopy = async () => {
    if (!hasText) return;
    try {
      await navigator.clipboard.writeText(message.message);
    } catch {
      // ignore
    }
    onClose?.();
  };

  const clampedX = Math.min(Math.max(position.x, 12), window.innerWidth - 220);
  const clampedY = Math.min(Math.max(position.y, 12), window.innerHeight - 280);

  return createPortal(
    <div
      ref={menuRef}
      className="bc-msg-menu"
      style={{ top: clampedY, left: clampedX }}
      role="menu"
    >
      <div className="bc-msg-menu-reactions">
        {QUICK_REACTIONS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            className={`bc-msg-menu-emoji ${myReactions.has(emoji) ? "bc-msg-menu-emoji-active" : ""}`}
            onClick={() => {
              onReact?.(messageKey, emoji);
              onClose?.();
            }}
            aria-label={`React with ${emoji}`}
          >
            {emoji}
          </button>
        ))}
      </div>
      <div className="bc-msg-menu-actions">
        {canReply && message._id && (
          <button
            type="button"
            className="bc-msg-menu-item"
            onClick={() => {
              onReply?.(message);
              onClose?.();
            }}
          >
            <CornerUpLeft size={14} />
            Reply
          </button>
        )}
        {hasText && (
          <button type="button" className="bc-msg-menu-item" onClick={handleCopy}>
            <Copy size={14} />
            Copy
          </button>
        )}
        {!isMine && (
          <button
            type="button"
            className="bc-msg-menu-item"
            onClick={() => {
              onReport?.(messageKey);
              onClose?.();
            }}
          >
            <Flag size={14} />
            Report
          </button>
        )}
        {isMine && message._id && (
          <button
            type="button"
            className="bc-msg-menu-item bc-msg-menu-item-danger"
            onClick={() => {
              onDelete?.(message._id);
              onClose?.();
            }}
          >
            <Trash2 size={14} />
            Delete
          </button>
        )}
      </div>
    </div>,
    document.body
  );
};

export default MessageActionMenu;
